/**
 * Configurazione per ottimizzazioni performance HTTP/2 e Critical Path
 */

// Configura preload per risorse critiche
export const preloadResources = [
	{ rel: 'preload', href: '/favicon.png', as: 'image' },
	{ rel: 'preload', href: '/robots.txt', as: 'fetch', crossorigin: 'anonymous' },
	// Preload critical city images (first 4 are above the fold)
	{ rel: 'preload', href: 'https://assets.auting.it/images/500/bologna.jpg', as: 'image' },
	{ rel: 'preload', href: 'https://assets.auting.it/images/500/firenze.jpg', as: 'image' },
	{ rel: 'preload', href: 'https://assets.auting.it/images/500/cagliari.jpg', as: 'image' },
	{ rel: 'preload', href: 'https://assets.auting.it/images/500/bari.jpg', as: 'image' }
];

// Configura prefetch per risorse future
export const prefetchResources = [
	'/en/login',
	'/en/register',
	'/en/owner',
	'/en/driver',
	// Prefetch remaining city images
	'https://assets.auting.it/images/500/roma.jpg',
	'https://assets.auting.it/images/500/milano.jpg',
	'https://assets.auting.it/images/500/palermo.jpg',
	'https://assets.auting.it/images/500/catania.jpg'
];

// Configura preconnect per domini esterni
export const preconnectDomains = [
	'https://api.auting.it',
	'https://assets.auting.it',
	'https://scopri.auting.it',
	// Note: No Google Fonts needed - using @fontsource (self-hosted)
	// LCP Optimization: Add third-party analytics domains
	'https://cdn.growthbook.io',
	'https://www.google-analytics.com',
	'https://www.googletagmanager.com',
	'https://connect.facebook.net',
	'https://script.hotjar.com',
	'https://vars.hotjar.com',
	'https://refersion.com'
];

// Configura Resource Hints per HTTP/2
export const resourceHints = {
	preload: preloadResources,
	prefetch: prefetchResources,
	preconnect: preconnectDomains
};

// Funzione per ottimizzare immagini con lazy loading e responsive images
export function optimizeImage(src, alt, className = '', priority = false) {
	// Generate responsive image URLs
	const getResponsiveImageUrl = (baseUrl, width) => {
		return baseUrl.replace('/500/', `/${width}/`);
	};
	
	return {
		src,
		alt,
		className,
		loading: priority ? 'eager' : 'lazy',
		decoding: 'async',
		srcset: `${getResponsiveImageUrl(src, 300)} 300w, ${getResponsiveImageUrl(src, 500)} 500w, ${getResponsiveImageUrl(src, 800)} 800w`,
		sizes: "(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 300px"
	};
}

// Funzione per caricare script non-critici in modo asincrono
export function loadScriptAsync(src, onLoad) {
	const script = document.createElement('script');
	script.src = src;
	script.async = true;
	if (onLoad) script.onload = onLoad;
	document.head.appendChild(script);
	return script;
}

// Funzione per caricare CSS non-critici in modo asincrono
export function loadStyleAsync(href, media = 'all') {
	const link = document.createElement('link');
	link.rel = 'stylesheet';
	link.href = href;
	link.media = 'print';
	link.onload = () => { link.media = media; };
	document.head.appendChild(link);
	return link;
}

// Configura headers per HTTP/2 Server Push
export const serverPushHeaders = {
	'Cache-Control': 'public, max-age=31536000, immutable',
	'Accept-Encoding': 'gzip, deflate, br',
	'Connection': 'keep-alive',
	'X-Content-Type-Options': 'nosniff',
	'X-Frame-Options': 'DENY',
	'X-XSS-Protection': '1; mode=block'
};

// Critical Path ottimizzazioni
export const criticalPathOptimizations = {
	// Inline critical CSS threshold
	inlineCssThreshold: 14000, // 14KB
	
	// Defer non-critical scripts
	deferScripts: [
		'google-analytics',
		'hotjar',
		'facebook-pixel',
		'refersion'
	],
	
	// Preload key resources
	preloadFonts: [
		'https://cdn.jsdelivr.net/npm/@fontsource/inter/files/inter-latin-400-normal.woff2',
		'https://cdn.jsdelivr.net/npm/@fontsource-variable/grandstander/files/grandstander-latin-wght-normal.woff2'
	],
	
	// Resource hints priorities
	resourcePriorities: {
		fonts: 'high',
		images: 'low',
		scripts: 'low',
		styles: 'high'
	}
};

export default {
	resourceHints,
	optimizeImage,
	loadScriptAsync,
	loadStyleAsync,
	serverPushHeaders,
	criticalPathOptimizations
};

// LCP (Largest Contentful Paint) Optimization Functions
export class LCPOptimizer {
	constructor() {
		this.criticalImages = new Set();
		this.preloadedResources = new Set();
	}

	/**
	 * Identify and preload the largest contentful paint candidate
	 */
	optimizeLCP() {
		// Wait for DOM to be ready
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.findAndPreloadLCPCandidate());
		} else {
			this.findAndPreloadLCPCandidate();
		}
	}

	/**
	 * Find LCP candidate elements and preload them
	 */
	findAndPreloadLCPCandidate() {
		// Common LCP candidate selectors
		const lcpSelectors = [
			'.hero img',
			'.hero-section img',
			'img[loading="eager"]',
			'[data-lcp="true"]',
			'header img',
			'.banner img',
			'main img:first-of-type'
		];

		let largestElement = null;
		let largestSize = 0;

		lcpSelectors.forEach(selector => {
			const elements = document.querySelectorAll(selector);
			elements.forEach(element => {
				const rect = element.getBoundingClientRect();
				const size = rect.width * rect.height;
				
				// Only consider elements in initial viewport
				if (rect.top < window.innerHeight && size > largestSize) {
					largestSize = size;
					largestElement = element;
				}
			});
		});

		if (largestElement) {
			this.preloadLCPResource(largestElement);
		}
	}

	/**
	 * Preload the LCP resource with optimal settings
	 */
	preloadLCPResource(element) {
		const src = element.src || element.dataset.src;
		if (!src || this.preloadedResources.has(src)) return;

		if (element.tagName === 'IMG') {
			// Create preload link for image
			const preloadLink = document.createElement('link');
			preloadLink.rel = 'preload';
			preloadLink.as = 'image';
			preloadLink.href = src;
			preloadLink.fetchPriority = 'high';
			document.head.appendChild(preloadLink);

			// Optimize the image element itself
			element.decoding = 'sync'; // Synchronous decoding for LCP
			element.fetchPriority = 'high';
			element.loading = 'eager';

			this.preloadedResources.add(src);
			this.criticalImages.add(element);
		}
	}

	/**
	 * Optimize font loading to prevent LCP delays
	 */
	optimizeFontLoading() {
		// Preload critical fonts
		const criticalFonts = criticalPathOptimizations.preloadFonts;
		
		criticalFonts.forEach(fontUrl => {
			if (!this.preloadedResources.has(fontUrl)) {
				const link = document.createElement('link');
				link.rel = 'preload';
				link.as = 'font';
				link.href = fontUrl;
				link.type = 'font/woff2';
				link.crossOrigin = 'anonymous';
				document.head.appendChild(link);
				this.preloadedResources.add(fontUrl);
			}
		});
	}

	/**
	 * Initialize all LCP optimizations
	 */
	initialize() {
		this.optimizeLCP();
		this.optimizeFontLoading();
	}
}

// Auto-initialize LCP optimizer if in browser environment
if (typeof window !== 'undefined') {
	const lcpOptimizer = new LCPOptimizer();
	
	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => lcpOptimizer.initialize());
	} else {
		lcpOptimizer.initialize();
	}
}
